/**
 * CustomerStatusConfigurable — Phase E configurable renderer
 *
 * Reads status_page_data from the org config (loaded at partner-switch time
 * into state.orgConfigData.organization) and renders a tab-based customer
 * status page.  Falls back to `props.fallback` when no org config exists,
 * so all production partners on explicit switch arms are unaffected.
 *
 * Tab codes map to the standard shared tab components used across partner-apps.
 */
import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Tabs, Tab, CircularProgress,
  Chip, Avatar,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PoliciesIcon from '@mui/icons-material/DescriptionOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import ClaimsIcon from '@mui/icons-material/AssignmentOutlined';

// Lazy-load the standard shared tab content components
import Policies from '../../components/Policies/index.es6';
import Premiums from '../../components/Premiums/index.es6';
import Claims from '../../components/Claims/index.es6';
import { getPartner, hasAuthourity } from '../../lib/access.es6';
import { getCustomer } from '../Customer/actions.es6';
import { getAllPoliciesRequest, getAllPremiumsRequest, getAllCustomerClaimsRequest } from '../CustomerScreen/actions.es6';

// Default accent if none configured
var DEFAULT_ACCENT = '#FF8349';

// Icons keyed by tab code
var TAB_ICONS = {
  profile: <PersonOutlineIcon sx={{ fontSize: 16 }} />,
  policies: <PoliciesIcon sx={{ fontSize: 16 }} />,
  payments: <PaymentsIcon sx={{ fontSize: 16 }} />,
  premiums: <PaymentsIcon sx={{ fontSize: 16 }} />,
  claims: <ClaimsIcon sx={{ fontSize: 16 }} />,
};

// Find the best customer status config for this org
function resolveConfig(orgConfigData) {
  var org = (orgConfigData && orgConfigData.organization) ? orgConfigData.organization : null;
  if (!org) return null;
  var configs = org.customer_status_configs || [];
  for (var i = 0; i < configs.length; i++) {
    if (configs[i].active !== false) return configs[i];
  }
  return configs.length ? configs[0] : null;
}

// Render content for a given tab code
function renderTabContent(code, props) {
  var customerGuid = props.customerGuid;

  switch (code) {
    case 'policies':
      return (
        <Box sx={{ pt: 2 }}>
          <Policies
            customer={{ guid: customerGuid }}
            customerGuid={customerGuid}
            {...props}
          />
        </Box>
      );
    case 'premiums':
      return (
        <Box sx={{ pt: 2 }}>
          <Premiums customerGuid={customerGuid} {...props} />
        </Box>
      );
    case 'claims':
      return (
        <Box sx={{ pt: 2 }}>
          <Claims customerGuid={customerGuid} {...props} />
        </Box>
      );
    case 'profile':
      return (
        <Box sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
            Customer profile information.
          </Typography>
        </Box>
      );
    case 'payments':
      return (
        <Box sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
            Payment history.
          </Typography>
        </Box>
      );
    default:
      return (
        <Box sx={{ pt: 3, textAlign: 'center', color: '#9CA3AF' }}>
          <Typography sx={{ fontSize: 13 }}>
            Content for &quot;{code}&quot; tab is not yet configured.
          </Typography>
        </Box>
      );
  }
}

class CustomerStatusConfigurable extends React.Component {
  constructor(props) {
    super(props);
    this.state = { activeTab: 0 };
    this.handleTabChange = this.handleTabChange.bind(this);
  }

  componentWillMount() {
    var customerGuid = this.props.customerGuid;
    if (customerGuid) {
      this.props.dispatch(getCustomer(customerGuid));
      this.props.dispatch(getAllPoliciesRequest({ customerGuid: customerGuid }));
      this.props.dispatch(getAllPremiumsRequest({ customerGuid: customerGuid }));
      this.props.dispatch(getAllCustomerClaimsRequest({ customerGuid: customerGuid }));
    }
  }

  handleTabChange(event, newValue) {
    this.setState({ activeTab: newValue });
  }

  render() {
    var orgData = this.props.orgConfigData || {};

    // Loading
    if (orgData.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: DEFAULT_ACCENT }} />
        </Box>
      );
    }

    var config = resolveConfig(orgData);

    // No config — use fallback (existing CustomerStatusDefault)
    if (!config) {
      if (this.props.fallback) return this.props.fallback;
      var partner = getPartner();
      return (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <PersonOutlineIcon sx={{ fontSize: 56, color: '#D1D5DB', mb: 2 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
            Customer Status Not Configured
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#9CA3AF', mt: 1, maxWidth: 380, mx: 'auto' }}>
            No customer status page config is set up for{' '}
            {partner ? partner.name : 'this partner'}.
            Configure one in the Organization → Customer Status tab.
          </Typography>
        </Box>
      );
    }

    var spd = config.status_page_data || {};
    var tabs = (spd.tabs || []).filter(function(t) {
      if (t.visible === false) return false;
      if (t.permission && !hasAuthourity(t.permission)) return false;
      return true;
    });
    var branding = spd.branding || {};
    var accentColor = branding.accent_color || DEFAULT_ACCENT;
    var activeTab = Math.min(this.state.activeTab, tabs.length - 1);

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Customer header */}
        <Box sx={{
          px: 3, py: 2,
          borderBottom: '1px solid #F3F4F6',
          display: 'flex', alignItems: 'center', gap: 2,
          bgcolor: '#FAFAFA',
        }}>
          <Avatar
            sx={{
              width: 40, height: 40,
              bgcolor: accentColor,
              fontSize: 16, fontWeight: 700,
            }}
          >
            {this.props.customerName
              ? this.props.customerName.charAt(0).toUpperCase()
              : '?'}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
              {this.props.customerName || 'Customer'}
            </Typography>
            {this.props.customerPhone && (
              <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                {this.props.customerPhone}
              </Typography>
            )}
          </Box>
          {spd.label && (
            <Chip
              label={spd.label}
              size="small"
              sx={{
                ml: 'auto', height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: '#F5F3FF', color: '#6D28D9',
                border: '1px solid #DDD6FE',
              }}
            />
          )}
        </Box>

        {/* Tab bar */}
        <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
          <Tabs
            value={activeTab}
            onChange={this.handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': { backgroundColor: accentColor },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 500,
                color: '#6B7280',
                minHeight: 44,
                py: 0,
              },
              '& .Mui-selected': {
                color: accentColor + ' !important',
                fontWeight: 700,
              },
            }}
          >
            {tabs.map(function(tab) {
              return (
                <Tab
                  key={tab.code}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {TAB_ICONS[tab.code] || null}
                      {tab.label || tab.code}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Tab content */}
        <Box sx={{ flex: 1, px: 3, py: 2, overflow: 'auto' }}>
          {tabs.length > 0 && activeTab < tabs.length && (
            renderTabContent(tabs[activeTab].code, this.props)
          )}
          {tabs.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ fontSize: 14, color: '#9CA3AF' }}>
                No tabs are visible in this configuration.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
}

export default connect(function(state) {
  return {
    orgConfigData: state.orgConfigData,
    customerGuid: state.currentCustomer && state.currentCustomer.guid,
    customerName: state.currentCustomer && (
      state.currentCustomer.first_name + ' ' + state.currentCustomer.last_name
    ),
    customerPhone: state.currentCustomer && state.currentCustomer.msisdn,
  };
})(CustomerStatusConfigurable);
